import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  SelectChangeEvent,
  InputAdornment,
  Autocomplete,
  styled,
  Paper,
  Tooltip,
  IconButton,
  Button,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import {
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
  AssignmentTurnedIn as StatusIcon,
  Groups as GroupsIcon,
  Add as AddIcon,
  TaskAlt as TaskAltIcon,
  PeopleAlt as PeopleAltIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Kompakt filtre alanı için styled component
const CompactFilterContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  backgroundColor: theme.palette.background.paper,
  borderRadius: 8,
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  marginBottom: theme.spacing(2),
  overflowX: 'auto',
  width: '100%',
  '&::-webkit-scrollbar': { height: '6px' },
  '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '3px' },
  '&::-webkit-scrollbar-thumb': { background: '#c1c1c1', borderRadius: '3px' },
  '&::-webkit-scrollbar-thumb:hover': { background: '#a8a8a8' }
}));

// Kompakt form control
const CompactFormControl = styled(FormControl)(({ theme }) => ({
  minWidth: '160px',
  maxWidth: '220px',
  flexShrink: 0
}));

// Kompakt TextField
const CompactTextField = styled(TextField)(({ theme }) => ({
  minWidth: '180px',
  flexShrink: 0
}));

// TasksFilter Props tanımı
interface TasksFilterProps {
  statusFilter: string;
  personnelFilter: string;
  groupFilter: string;
  taskSearchTerm: string;
  showFilters: boolean; // Bu prop hala gelebilir ama artık kullanılmayacak
  personnel: any[];
  taskGroups: any[];
  onStatusFilterChange: (event: SelectChangeEvent) => void;
  onPersonnelFilterChange: (event: React.SyntheticEvent, newValue: any) => void;
  onGroupFilterChange: (event: React.SyntheticEvent, newValue: any) => void;
  onTaskSearchTermChange: (value: string) => void;
  onToggleFilters: () => void; // Bu prop hala gelebilir ama artık kullanılmayacak
  taskType: string;
  onCreateTeam?: () => void; // Ekip oluşturma modalını açmak için handler
  onCreateTeamTask?: () => void; // Ekip görevi ekleme modalını açmak için handler
  displayMode?: 'tasks' | 'teams'; // Görünüm modu seçimi için yeni prop
  onDisplayModeChange?: (event: React.MouseEvent<HTMLElement>, newMode: 'tasks' | 'teams' | null) => void; // Görünüm değiştirme için handler
  branchFilter?: string; // Şube filtresi değeri
  branches?: {[key: string]: string}; // Şube listesi
  onBranchFilterChange?: (event: SelectChangeEvent) => void; // Şube filtresi değişikliği için handler
}

// Durum filtresi için seçenekler
const statusOptions = [
  { value: 'all', label: 'Tüm Görevler' },
  { value: 'completed', label: 'Tamamlanan', color: '#4CAF50' },
  { value: 'accepted', label: 'Kabul Edilmiş', color: '#9C27B0' },
  { value: 'pending', label: 'Beklemede', color: '#FF9800' },
  { value: 'missed', label: 'Tamamlanmamış', color: '#F44336' },
  { value: 'rejected', label: 'Reddedildi', color: '#F44336' },
];

const TasksFilter: React.FC<TasksFilterProps> = ({
  statusFilter,
  personnelFilter,
  groupFilter,
  taskSearchTerm,
  personnel,
  taskGroups,
  onStatusFilterChange,
  onPersonnelFilterChange,
  onGroupFilterChange,
  onTaskSearchTermChange,
  taskType,
  onCreateTeam,
  onCreateTeamTask,
  displayMode = 'tasks',
  onDisplayModeChange,
  branchFilter,
  branches,
  onBranchFilterChange
}) => {
  // Translation hook'unu ekle
  const { t } = useTranslation('common2');
  
  // Personnel değişikliği için handler
  const handlePersonnelChange = (event: React.SyntheticEvent, newValue: any) => {
    onPersonnelFilterChange(event, newValue);
  };

  return (
    <CompactFilterContainer>
      {/* Durum Filtresi */}
      <CompactFormControl size="small">
        <Tooltip title={t('tasks.filters.status')}>
          <InputLabel id="status-filter-label">
            <StatusIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
            {t('tasks.filters.status')}
          </InputLabel>
        </Tooltip>
        <Select
          labelId="status-filter-label"
          value={statusFilter}
          label={
            <>
              <StatusIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              {t('tasks.filters.status')}
            </>
          }
          onChange={onStatusFilterChange}
          size="small"
        >
          <MenuItem value="all">{t('tasks.filters.allTasks')}</MenuItem>
          <MenuItem value="pending">{t('tasks.filters.pending')}</MenuItem>
          <MenuItem value="accepted">{t('tasks.filters.accepted')}</MenuItem>
          <MenuItem value="started">{t('tasks.status.started')}</MenuItem>
          <MenuItem value="completed">{t('tasks.filters.completed')}</MenuItem>
          <MenuItem value="missed">{t('tasks.filters.missed')}</MenuItem>
          <MenuItem value="rejected">{t('tasks.filters.rejected')}</MenuItem>
        </Select>
      </CompactFormControl>

      {/* Personel Filtresi */}
      <CompactFormControl size="small">
        <Autocomplete
          size="small"
          options={personnel}
          getOptionLabel={(option) => option.name || ''}
          value={personnel.find(p => p.id === personnelFilter) || null}
          onChange={handlePersonnelChange}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t('tasks.filters.personnel')}
              label={t('tasks.filters.personnel')}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          )}
          renderOption={(props, option) => (
            <MenuItem {...props}>
              {option.name}
            </MenuItem>
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          noOptionsText={t('tasks.filters.allPersonnel')}
          clearText={t('tasks.filters.clearFilters')}
          openText={t('tasks.filters.toggleFilters')}
          closeText={t('tasks.filters.toggleFilters')}
        />
      </CompactFormControl>

      {/* Görev Arama */}
      <CompactTextField
        size="small"
        placeholder={t('tasks.filters.search')}
        value={taskSearchTerm}
        onChange={(e) => onTaskSearchTermChange(e.target.value)}
        label={t('tasks.filters.search')}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      {/* Grup Filtresi - Eğer gruplar varsa göster */}
      {taskGroups.length > 0 && (
        <CompactFormControl size="small">
          <Autocomplete
            size="small"
            options={taskGroups}
            getOptionLabel={(option) => option.name || ''}
            value={taskGroups.find(g => g.id === groupFilter) || null}
            onChange={onGroupFilterChange}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('tasks.filters.group')}
                label={t('tasks.filters.group')}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <CategoryIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <MenuItem {...props}>
                {option.name}
              </MenuItem>
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={t('tasks.filters.allGroups')}
            clearText={t('tasks.filters.clearFilters')}
            openText={t('tasks.filters.toggleFilters')}
            closeText={t('tasks.filters.toggleFilters')}
          />
        </CompactFormControl>
      )}

      {/* Şube Filtresi - branches ve onBranchFilterChange props'ları varsa göster */}
      {branches && onBranchFilterChange && (
        <CompactFormControl size="small">
          <Tooltip title={t('tasks.filters.branch')}>
            <InputLabel id="branch-filter-label">
              <BusinessIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              {t('tasks.filters.branch')}
            </InputLabel>
          </Tooltip>
          <Select
            labelId="branch-filter-label"
            value={branchFilter || 'all'}
            label={
              <>
                <BusinessIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                {t('tasks.filters.branch')}
              </>
            }
            onChange={onBranchFilterChange}
            size="small"
          >
            <MenuItem value="all">{t('tasks.filters.allBranches')}</MenuItem>
            <MenuItem value="unassigned">{t('tasks.filters.unassigned')}</MenuItem>
            {Object.entries(branches).map(([id, name]) => (
              <MenuItem key={id} value={id}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </CompactFormControl>
      )}

      {/* Ekip Görevleri sayfasında ise butonları göster */}
      {taskType === 'team' && (
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          {/* Görünüm değiştirme butonları */}
          {onDisplayModeChange && (
            <ToggleButtonGroup
              value={displayMode}
              exclusive
              onChange={onDisplayModeChange}
              aria-label="görünüm modu"
              size="small"
              sx={{ mr: 1 }}
            >
              <ToggleButton value="tasks" aria-label="görevler">
                <TaskAltIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                {t('tasks.taskTypes.normal')}
              </ToggleButton>
              <ToggleButton value="teams" aria-label="ekipler">
                <PeopleAltIcon sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                {t('tasks.taskTypes.team')}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            sx={{ 
              height: 40, 
              borderRadius: 2, 
              whiteSpace: 'nowrap'
            }}
            onClick={onCreateTeamTask}
          >
            {t('tasks.team.addTeamTask')}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<GroupsIcon />}
            sx={{ 
              height: 40, 
              borderRadius: 2, 
              whiteSpace: 'nowrap'
            }}
            onClick={onCreateTeam}
          >
            {t('tasks.team.createTeam')}
          </Button>
        </Box>
      )}
    </CompactFilterContainer>
  );
};

export default TasksFilter; 